/**
 * Live case management — the lower half of the Case Overview page. The calm
 * workspace for a (possibly months-long) incident: the two-date model + clock,
 * the cached "where we stand" briefing (free reads; one AI call to refresh),
 * AI paste-&-autofill, forensic-firm/parties tracking, workstreams, action
 * items, and flexible updates (any type/cadence/none). The human owns every
 * legal judgment. Rendered directly under the case facts — not a separate tab.
 */
import React, { useState, useEffect, useCallback } from 'react';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';

const API = process.env.REACT_APP_API_URL || '';

async function api(path, { method = 'GET', body } = {}) {
  const res = await fetch(`${API}/api/privacy${path}`, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.message || 'Something went wrong.');
  return json;
}

// Multipart upload (the api() helper is JSON only).
async function uploadFile(path, file, extra = {}) {
  const fd = new FormData();
  fd.append('file', file);
  Object.entries(extra).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, v); });
  const res = await fetch(`${API}/api/privacy${path}`, { method: 'POST', credentials: 'include', body: fd });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.message || 'Upload failed.');
  return json;
}

const DOC_CATS = [
  ['forensic_report', 'Forensic report'], ['mitigation_evidence', 'Mitigation evidence'],
  ['ba_communication', 'Vendor / BA communication'], ['audit_log', 'Audit log'],
  ['ocr_submission_proof', 'OCR submission proof'], ['notification_proof', 'Notification proof'],
  ['other', 'Other'],
];

const UPDATE_TYPES = [
  ['note', 'Note'], ['call', 'Call'], ['email', 'Email'], ['meeting', 'Meeting'],
  ['webinar', 'Recorded webinar'], ['forensic_report', 'Forensic report'],
  ['vendor_update', 'Vendor/BA update'], ['memo', 'Memo'], ['other', 'Other'],
];
const PARTY_ROLES = [['forensic', 'Forensic firm'], ['counsel', 'Outside counsel'], ['vendor_ba', 'Vendor / BA'], ['law_enforcement', 'Law enforcement'], ['other', 'Other']];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

const card = 'bg-white border border-slate-200 rounded-2xl p-5';
const input = 'w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200';
const btn = 'px-3 py-1.5 rounded-lg text-sm font-medium';

export default function PrivacyWarRoom({ incident, incidentId, onRefresh, canEdit }) {
  const [updates, setUpdates] = useState([]);
  const [actions, setActions] = useState([]);
  const [streams, setStreams] = useState([]);
  const [parties, setParties] = useState([]);
  const [docs, setDocs] = useState([]);
  const [memory, setMemory] = useState(null);
  const [dates, setDates] = useState({
    incident_known_date: incident?.incident_known_date || incident?.discovery_date || '',
    breach_discovered_date: incident?.breach_discovered_date || '',
    breach_determined_date: incident?.breach_determined_date || '',
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api(`/incidents/${incidentId}/updates`), api(`/incidents/${incidentId}/action-items`),
      api(`/incidents/${incidentId}/workstreams`), api(`/incidents/${incidentId}/parties`),
      api(`/incidents/${incidentId}/memory`), api(`/incidents/${incidentId}/documents`),
    ]).then(([u, a, w, p, m, dz]) => {
      setUpdates(u.updates || []); setActions(a.action_items || []);
      setStreams(w.workstreams || []); setParties(p.parties || []); setMemory(m.memory || null);
      setDocs(dz.documents || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [incidentId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-slate-400 text-center py-12">Loading the case…</p>;

  const clockAnchor = dates.breach_discovered_date || dates.breach_determined_date || null;
  const clockRunning = !!clockAnchor;
  const notifyBy = incident?.notification_deadline_date
    ? fmtDate(incident.notification_deadline_date)
    : clockAnchor
      ? fmtDate(new Date(new Date(clockAnchor).getTime() + 60 * 86400000))
      : '—';

  return (
    <div className="space-y-5">
      {/* Stage banner — the three-date clock model (164.404(a)(2)) */}
      <div className={`rounded-2xl px-5 py-4 border ${clockRunning ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100'}`}>
        <div className="text-sm font-semibold text-slate-800">
          {clockRunning ? `Clock running — notify by ${notifyBy}` : 'Under diligence — no notification clock yet'}
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {clockRunning
            ? 'The clock runs from the day the breach was known or should have been known (45 CFR 164.404(a)(2)), whether or not the formal determination is finished. The notify-by date reflects the earliest applicable clock across the mapped states.'
            : 'Awareness of an incident starts the duty of reasonable diligence, not the clock. Record the breach-discovered date the moment the breach becomes known; waiting for the formal determination does not extend the deadline.'}
        </p>
        <DateRow incidentId={incidentId} dates={dates} setDates={setDates} canEdit={canEdit} onRefresh={onRefresh} />
      </div>

      {/* Divider — above is the record (facts + dates); below is the live work. */}
      <div className="flex items-center gap-3 pt-1">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Live case management</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>

      {/* Where we stand (cached; free read) */}
      <WhereWeStand incidentId={incidentId} memory={memory} setMemory={setMemory} canEdit={canEdit} />

      {/* AI paste & autofill */}
      {canEdit && <PasteAutofill incidentId={incidentId} onApplied={load} />}

      {/* Forensic / external parties */}
      <Parties incidentId={incidentId} parties={parties} reload={load} canEdit={canEdit} />

      {/* Case documents — the single place to add files; the mitigation scan reads them */}
      <CaseDocuments incidentId={incidentId} docs={docs.filter((d) => !d.source_update_id)} reload={load} canEdit={canEdit} />

      {/* Workstreams */}
      <Workstreams incidentId={incidentId} streams={streams} reload={load} canEdit={canEdit} />

      {/* Action items */}
      <ActionItems incidentId={incidentId} actions={actions} reload={load} canEdit={canEdit} />

      {/* Flexible case updates */}
      <Updates incidentId={incidentId} updates={updates} docs={docs} reload={load} canEdit={canEdit} />
    </div>
  );
}

function DateRow({ incidentId, dates, setDates, canEdit, onRefresh }) {
  // Edit locally, save on commit (blur or Enter). Saving on every change
  // event refreshed the whole case mid-keystroke while the year was being
  // typed, yanking focus out of the field.
  const iso = (v) => (v ? String(v).slice(0, 10) : '');
  const [draft, setDraft] = useState({
    incident_known_date: iso(dates.incident_known_date),
    breach_discovered_date: iso(dates.breach_discovered_date),
    breach_determined_date: iso(dates.breach_determined_date),
  });
  useEffect(() => {
    setDraft({
      incident_known_date: iso(dates.incident_known_date),
      breach_discovered_date: iso(dates.breach_discovered_date),
      breach_determined_date: iso(dates.breach_determined_date),
    });
  }, [dates.incident_known_date, dates.breach_discovered_date, dates.breach_determined_date]);

  const commit = async (key) => {
    const next = draft[key] || null;
    if (next === (iso(dates[key]) || null)) return; // unchanged → no save, no refresh
    try {
      const r = await api(`/incidents/${incidentId}/case-dates`, { method: 'PATCH', body: { [key]: next } });
      setDates((d) => ({ ...d, ...r.dates }));
      onRefresh && onRefresh();
    } catch (e) { alert(e.message); }
  };

  const field = (key, label) => (
    <label className="text-xs text-slate-600">{label}
      <input
        type="date"
        disabled={!canEdit}
        value={draft[key]}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
        onBlur={() => commit(key)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        className={input + ' mt-1'}
      />
    </label>
  );

  return (
    <div className="mt-3 grid sm:grid-cols-3 gap-3">
      {field('incident_known_date', 'Incident first known (starts diligence, not the clock)')}
      {field('breach_discovered_date', 'Breach discovered (starts the clock — when the breach was known or should have been)')}
      {field('breach_determined_date', 'Determination approved (workflow date, does not move the clock)')}
    </div>
  );
}

function WhereWeStand({ incidentId, memory, setMemory, canEdit }) {
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try { const r = await api(`/incidents/${incidentId}/memory/refresh`, { method: 'POST' }); setMemory(r.memory); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const structured = memory?.structured || {};
  return (
    <div className={card}>
      <div className="flex items-center">
        <h3 className="font-semibold text-slate-800">Where we stand</h3>
        <span className="ml-2 text-xs text-slate-400">
          {memory?.generated_at ? `as of ${fmtDateTime(memory.generated_at)}${memory.stale ? ' · new updates since' : ''}` : 'not generated yet'}
        </span>
        {canEdit && <button onClick={refresh} disabled={busy} className={btn + ' ml-auto bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50'}>{busy ? 'Refreshing…' : (memory ? 'Refresh' : 'Generate')}</button>}
      </div>
      {memory?.summary_md
        ? <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap leading-relaxed">{memory.summary_md}</p>
        : <p className="text-sm text-slate-400 mt-3">Add a few case updates, then generate the briefing. Reading it afterward is free — it only re-runs when you click Refresh.</p>}
      {(structured.outstanding?.length > 0 || structured.next_steps?.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4 mt-3 text-sm">
          {structured.outstanding?.length > 0 && <div><div className="text-xs font-semibold text-slate-500 uppercase">Outstanding</div><ul className="list-disc ml-5 text-slate-700 mt-1">{structured.outstanding.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
          {structured.next_steps?.length > 0 && <div><div className="text-xs font-semibold text-slate-500 uppercase">Next steps</div><ul className="list-disc ml-5 text-slate-700 mt-1">{structured.next_steps.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
        </div>
      )}
    </div>
  );
}

function PasteAutofill({ incidentId, onApplied }) {
  const [raw, setRaw] = useState('');
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState(null);
  const run = async () => {
    if (!raw.trim()) return;
    setBusy(true);
    try { const r = await api('/ai/extract', { method: 'POST', body: { rawText: raw } }); setFields(r.fields); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const applyDate = async () => { try { await api(`/incidents/${incidentId}/case-dates`, { method: 'PATCH', body: { incident_known_date: fields.incident_known_date } }); onApplied(); alert('Saved the known date.'); } catch (e) { alert(e.message); } };
  const logUpdate = async () => {
    try {
      await api(`/incidents/${incidentId}/updates`, { method: 'POST', body: { update_type: 'note', title: fields.title || 'Source document', summary: fields.what_happened || fields.summary } });
      onApplied(); setFields(null); setRaw(''); alert('Logged as a case update.');
    } catch (e) { alert(e.message); }
  };
  return (
    <div className={card}>
      <h3 className="font-semibold text-slate-800">Paste a source → AI reads it</h3>
      <p className="text-xs text-slate-500 mt-1">Paste the discovery email, vendor notice, or ticket. The AI proposes the facts (it never decides if it's a breach). Review, edit, then apply.</p>
      <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={4} placeholder="Paste the source document here…" className={input + ' mt-2 resize-none'} />
      <button onClick={run} disabled={busy || !raw.trim()} className={btn + ' mt-2 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40'}>{busy ? 'Reading…' : 'Read with AI'}</button>
      {fields && (
        <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-4 text-sm space-y-1">
          <div><span className="text-slate-400">Known date:</span> {fields.incident_known_date || '—'}</div>
          <div><span className="text-slate-400">Title:</span> {fields.title || '—'}</div>
          <div><span className="text-slate-400">What happened:</span> {fields.what_happened || fields.summary || '—'}</div>
          <div><span className="text-slate-400">PHI types:</span> {(fields.phi_types || []).join(', ') || '—'}</div>
          <div><span className="text-slate-400">Affected:</span> {fields.number_affected ?? '—'} · <span className="text-slate-400">Recipient:</span> {fields.recipient || '—'}</div>
          {fields.reasoning && <div className="text-xs text-slate-400 italic">{fields.reasoning}</div>}
          <div className="flex gap-2 pt-2">
            {fields.incident_known_date && <button onClick={applyDate} className={btn + ' bg-blue-50 text-blue-700 hover:bg-blue-100'}>Save known date</button>}
            <button onClick={logUpdate} className={btn + ' bg-blue-50 text-blue-700 hover:bg-blue-100'}>Log as case update</button>
          </div>
          <p className="text-[11px] text-slate-400 pt-1">You review and apply — the AI only proposes. Your edits teach it over time.</p>
        </div>
      )}
    </div>
  );
}

function Parties({ incidentId, parties, reload, canEdit }) {
  const [f, setF] = useState({ role: 'forensic', org_name: '', name: '', contact: '', expected_report_date: '' });
  const add = async () => { if (!f.org_name && !f.name) return; try { await api(`/incidents/${incidentId}/parties`, { method: 'POST', body: f }); setF({ role: 'forensic', org_name: '', name: '', contact: '', expected_report_date: '' }); reload(); } catch (e) { alert(e.message); } };
  return (
    <div className={card}>
      <h3 className="font-semibold text-slate-800">Forensic firm &amp; external parties</h3>
      <div className="mt-3 space-y-2">
        {parties.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 text-sm">
            <span className="text-xs font-medium text-slate-500">{(PARTY_ROLES.find((r) => r[0] === p.role) || [, p.role])[1]}</span>
            <span className="text-slate-700">{p.org_name || p.name}{p.name && p.org_name ? ` · ${p.name}` : ''}</span>
            {p.expected_report_date && <span className="ml-auto text-xs text-amber-700">report expected {fmtDate(p.expected_report_date)}</span>}
          </div>
        ))}
        {parties.length === 0 && <p className="text-sm text-slate-400">No external parties yet.</p>}
      </div>
      {canEdit && (
        <div className="mt-3 grid sm:grid-cols-5 gap-2">
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={input}>{PARTY_ROLES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          <input value={f.org_name} onChange={(e) => setF({ ...f, org_name: e.target.value })} placeholder="Firm/org" className={input} />
          <input value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} placeholder="Contact" className={input} />
          <input type="date" value={f.expected_report_date} onChange={(e) => setF({ ...f, expected_report_date: e.target.value })} className={input} title="Expected report date" />
          <button onClick={add} className={btn + ' bg-blue-600 text-white hover:bg-blue-700'}>Add</button>
        </div>
      )}
    </div>
  );
}

function Workstreams({ incidentId, streams, reload, canEdit }) {
  const [label, setLabel] = useState('');
  const add = async () => { if (!label.trim()) return; try { await api(`/incidents/${incidentId}/workstreams`, { method: 'POST', body: { label } }); setLabel(''); reload(); } catch (e) { alert(e.message); } };
  const setStatus = async (id, status) => { try { await api(`/workstreams/${id}`, { method: 'PATCH', body: { status } }); reload(); } catch (e) { alert(e.message); } };
  return (
    <div className={card}>
      <h3 className="font-semibold text-slate-800">Open workstreams</h3>
      <div className="mt-3 flex flex-wrap gap-2">
        {streams.map((s) => (
          <div key={s.id} className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-100 text-sm">
            <span className="text-slate-700">{s.label}</span>
            {canEdit
              ? <select value={s.status} onChange={(e) => setStatus(s.id, e.target.value)} className="text-xs border border-slate-200 rounded-lg px-1 py-0.5 bg-white"><option value="open">Open</option><option value="in_progress">In progress</option><option value="blocked">Blocked</option><option value="done">Done</option></select>
              : <span className="text-xs text-slate-400">{s.status}</span>}
          </div>
        ))}
        {streams.length === 0 && <p className="text-sm text-slate-400">No workstreams yet (e.g. Forensic, Legal, Notification, Regulator, Comms).</p>}
      </div>
      {canEdit && <div className="mt-3 flex gap-2"><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Add a workstream (e.g. Forensic)" className={input} /><button onClick={add} className={btn + ' bg-blue-600 text-white hover:bg-blue-700'}>Add</button></div>}
    </div>
  );
}

function ActionItems({ incidentId, actions, reload, canEdit }) {
  const [f, setF] = useState({ title: '', owner_label: '', due_date: '' });
  const add = async () => { if (!f.title.trim()) return; try { await api(`/incidents/${incidentId}/action-items`, { method: 'POST', body: f }); setF({ title: '', owner_label: '', due_date: '' }); reload(); } catch (e) { alert(e.message); } };
  const setStatus = async (id, status) => { try { await api(`/action-items/${id}`, { method: 'PATCH', body: { status } }); reload(); } catch (e) { alert(e.message); } };
  return (
    <div className={card}>
      <h3 className="font-semibold text-slate-800">Action items</h3>
      <div className="mt-3 space-y-2">
        {actions.map((a) => (
          <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 text-sm">
            <span className={`text-slate-700 ${a.status === 'done' ? 'line-through text-slate-400' : ''}`}>{a.title}</span>
            <span className="text-xs text-slate-400">{a.owner_name || a.owner_label || 'unassigned'}{a.due_date ? ` · due ${fmtDate(a.due_date)}` : ''}</span>
            {canEdit
              ? <select value={a.status} onChange={(e) => setStatus(a.id, e.target.value)} className="ml-auto text-xs border border-slate-200 rounded-lg px-1 py-0.5 bg-white"><option value="open">Open</option><option value="in_progress">In progress</option><option value="blocked">Blocked</option><option value="done">Done</option><option value="cancelled">Cancelled</option></select>
              : <span className="ml-auto text-xs text-slate-400">{a.status}</span>}
          </div>
        ))}
        {actions.length === 0 && <p className="text-sm text-slate-400">No action items yet.</p>}
      </div>
      {canEdit && (
        <div className="mt-3 grid sm:grid-cols-4 gap-2">
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Action item" className={input + ' sm:col-span-2'} />
          <input value={f.owner_label} onChange={(e) => setF({ ...f, owner_label: e.target.value })} placeholder="Owner" className={input} />
          <div className="flex gap-2"><input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className={input} /><button onClick={add} className={btn + ' bg-blue-600 text-white hover:bg-blue-700'}>Add</button></div>
        </div>
      )}
    </div>
  );
}

// Standalone case documents shelf — the single place to add files. Each one is
// text-extracted so the mitigation scan can read it.
function CaseDocuments({ incidentId, docs, reload, canEdit }) {
  const [category, setCategory] = useState('forensic_report');
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadFile(`/incidents/${incidentId}/documents/upload`, file, { category });
      if (r?.document?.readable === false) alert(r.document.read_warning || `"${file.name}" was attached, but no readable text could be extracted, so the privacy agent cannot see its content.`);
      reload();
    }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm('Delete this document?')) return; try { await api(`/incidents/${incidentId}/documents/${id}`, { method: 'DELETE' }); reload(); } catch (e) { alert(e.message); } };
  const catLabel = (c) => (DOC_CATS.find((x) => x[0] === c) || [, c])[1];
  return (
    <div className={card}>
      <div className="flex items-center flex-wrap gap-2">
        <h3 className="font-semibold text-slate-800">Case documents</h3>
        <span className="ml-1 text-xs text-slate-400">forensic reports, meeting notes, vendor notices — uploaded here, read by the mitigation scan</span>
      </div>
      {canEdit && (
        <>
          <PhiFriendlyNote variant="upload" className="mt-3" />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={input + ' w-auto'}>{DOC_CATS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            <input type="file" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} className="text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200" />
            {busy && <span className="text-xs text-slate-400">Reading the file…</span>}
          </div>
        </>
      )}
      <div className="mt-3 space-y-1.5">
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-slate-50">
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{catLabel(d.category)}</span>
            <a href={`${API}/api/privacy/incidents/${incidentId}/documents/${d.id}/download`} className="font-medium text-slate-800 hover:text-blue-600 truncate">{d.file_name}</a>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${d.has_text ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{d.has_text ? 'readable' : 'no text'}</span>
            <span className="ml-auto text-xs text-slate-400">{fmtDate(d.created_at)}</span>
            {canEdit && <button onClick={() => del(d.id)} className="text-slate-300 hover:text-red-500 text-xs">✕</button>}
          </div>
        ))}
        {docs.length === 0 && <p className="text-sm text-slate-400">No case documents yet.</p>}
      </div>
    </div>
  );
}

function Updates({ incidentId, updates, docs = [], reload, canEdit }) {
  const [f, setF] = useState({ update_type: 'note', occurred_at: '', title: '', summary: '', link_url: '' });
  const [open, setOpen] = useState(false);
  const add = async () => {
    if (!f.summary.trim() && !f.title.trim() && !f.link_url.trim()) return;
    try { await api(`/incidents/${incidentId}/updates`, { method: 'POST', body: f }); setF({ update_type: 'note', occurred_at: '', title: '', summary: '', link_url: '' }); setOpen(false); reload(); } catch (e) { alert(e.message); }
  };
  const del = async (id) => { try { await api(`/incidents/${incidentId}/updates/${id}`, { method: 'DELETE' }); reload(); } catch (e) { alert(e.message); } };
  const attach = async (updateId, file) => {
    if (!file) return;
    try {
      const r = await uploadFile(`/incidents/${incidentId}/documents/upload`, file, { update_id: updateId, category: 'other' });
      if (r?.document?.readable === false) alert(r.document.read_warning || `"${file.name}" was attached, but no readable text could be extracted, so the privacy agent cannot see its content.`);
      reload();
    }
    catch (e) { alert(e.message); }
  };
  const label = (t) => (UPDATE_TYPES.find((u) => u[0] === t) || [, t])[1];
  return (
    <div className={card}>
      <div className="flex items-center">
        <h3 className="font-semibold text-slate-800">Case updates</h3>
        <span className="ml-2 text-xs text-slate-400">any kind — a call, an email, a recorded webinar, a forensic report, or none</span>
        {canEdit && <button onClick={() => setOpen(!open)} className="ml-auto text-sm text-blue-600 font-medium hover:text-blue-700">{open ? 'Cancel' : '+ Add update'}</button>}
      </div>
      {open && canEdit && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          <select value={f.update_type} onChange={(e) => setF({ ...f, update_type: e.target.value })} className={input}>{UPDATE_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          <input type="datetime-local" value={f.occurred_at} onChange={(e) => setF({ ...f, occurred_at: e.target.value })} className={input} />
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title (optional)" className={input + ' sm:col-span-2'} />
          <textarea value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} rows={2} placeholder="What happened / decisions" className={input + ' sm:col-span-2 resize-none'} />
          <input value={f.link_url} onChange={(e) => setF({ ...f, link_url: e.target.value })} placeholder="Link (e.g. recorded webinar)" className={input + ' sm:col-span-2'} />
          <button onClick={add} className={btn + ' bg-blue-600 text-white hover:bg-blue-700 w-fit'}>Save update</button>
        </div>
      )}
      <div className="mt-3 space-y-2">
        {updates.map((u) => {
          const atts = docs.filter((d) => d.source_update_id === u.id);
          return (
            <div key={u.id} className="p-3 rounded-xl border border-slate-100 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{label(u.update_type)}</span>
                {u.title && <span className="font-medium text-slate-800">{u.title}</span>}
                <span className="ml-auto text-xs text-slate-400">{fmtDateTime(u.occurred_at || u.created_at)}</span>
                {canEdit && <button onClick={() => del(u.id)} className="text-slate-300 hover:text-red-500 text-xs ml-2">✕</button>}
              </div>
              {u.summary && <p className="text-slate-700 mt-1">{u.summary}</p>}
              {u.link_url && <a href={u.link_url} target="_blank" rel="noreferrer" className="text-blue-600 text-xs hover:underline">{u.link_url}</a>}
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                {atts.map((d) => (
                  <a key={d.id} href={`${API}/api/privacy/incidents/${incidentId}/documents/${d.id}/download`} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-blue-600">📎 {d.file_name}{d.has_text ? '' : ' (no text)'}</a>
                ))}
                {canEdit && (
                  <label className="inline-flex items-center text-xs text-blue-600 cursor-pointer hover:text-blue-700">
                    + Attach file
                    <input type="file" className="hidden" onChange={(e) => attach(u.id, e.target.files?.[0])} />
                  </label>
                )}
              </div>
            </div>
          );
        })}
        {updates.length === 0 && <p className="text-sm text-slate-400">No updates logged yet.</p>}
      </div>
    </div>
  );
}
